import { domIsChildOf } from 'pf-frontend-common/src/helper/dom/is-child-of';
import { domQuerySelectorAll } from 'pf-frontend-common/src/helper/dom/query-selector-all';
import { objectMerge } from 'pf-frontend-common/src/helper/object/merge';
import { BrowserViewport } from 'pf-frontend-common/src/module/browser/viewport';
import { BrowserViewportAwareInterface } from 'pf-frontend-common/src/module/browser/viewport-aware.interface';
import { DataKeyValueStringInterface } from 'pf-frontend-common/src/module/data/key-value/string.interface';
import { DomElementEvent } from 'pf-frontend-common/src/module/dom/element.event';
import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';
import { StatsContextLocalInterface } from 'pf-frontend-common/src/module/stats/context/local.interface';
import { StatsEntityEnum } from 'pf-frontend-common/src/module/stats/entity.enum';
import { StatsAttributionServiceInterface } from 'pf-frontend-common/src/service/stats-attribution/service.interface';
import { StatsDataServiceInterface } from 'pf-frontend-common/src/service/stats-data/service.interface';
import { WindowServiceInterface } from 'pf-frontend-common/src/service/window/service.interface';

import { AgentModel } from 'common/data/agent/model';
import { AgentStatsDataAdapter } from 'common/data/agent/stats/data-adapter';
import { LeadStatsLeadContextAdapter } from 'common/data/lead/stats/lead-context-adapter';
import { AgentCardCommonTemplatePropsInterface } from 'common/module/agent/card-common/template-props.interface';
import { AgentListCommonStrategyEvent } from 'common/module/agent/list-common/strategy.event';
import { AgentListCommonStrategyInterface } from 'common/module/agent/list-common/strategy.interface';
import { AgentListCommonTemplatePropsInterface } from 'common/module/agent/list-common/template-props.interface';
import { DataStore } from 'common/module/data/store';
import { DataStoreOptionsInterface } from 'common/module/data/store/options.interface';
import { LeadEmailForm } from 'common/module/lead/email/form';
import { LeadEmailFormEvent } from 'common/module/lead/email/form.event';
import { LeadEmailFormInputInterface } from 'common/module/lead/email/form/input.interface';
import { StatsServiceInterface } from 'common/service/stats/service.interface';

import { AgentListCommonAdInfoInterface } from './ad-info.interface';

/**
 * TODO-FE[WEB-4450]: Rename parent directory and change this to AgentListViewStore
 * TODO-FE[WEB-4450]: Refactor this view store to accommodate fully featured agent list view store
 *                    (mobile and desktop and variations)
 */
export class AgentListCommonViewStore extends DataStore implements BrowserViewportAwareInterface {
  /**
   * @inheritDoc
   */
  protected state: AgentListCommonTemplatePropsInterface;

  /**
   * CSS Selectors
   */
  protected cssSelector: DataKeyValueStringInterface = {
    cta: '[data-qs="cta"]',
  };

  /**
   * Stats local context
   */
  protected statsContextLocal: StatsContextLocalInterface = {};

  /**
   * Strategy used to specialize some methods
   */
  protected strategy: AgentListCommonStrategyInterface;

  /**
   * Constructor
   */
  public constructor(
    eventEmitter: EventEmitterInterface,
    private windowService: WindowServiceInterface,
    private leadEmailForm: LeadEmailForm,
    private statsService: StatsServiceInterface,
    private statsDataService: StatsDataServiceInterface,
    private statsAttributionService: StatsAttributionServiceInterface,
    private agentStatsDataAdapter: AgentStatsDataAdapter,
    private browserViewport: BrowserViewport
  ) {
    super(eventEmitter);
  }

  /**
   * @inheritDoc
   */
  public getBrowserViewport(): BrowserViewport {
    return this.browserViewport;
  }

  /**
   * Set the strategy
   */
  public setStrategy(strategy: AgentListCommonStrategyInterface): void {
    // Existing strategy
    if (this.strategy) {
      // Detach previous listener
      this.strategy
        .getEventEmitter()
        .removeListener(AgentListCommonStrategyEvent.updateAgentCards, this.onUpdateAgentCardsStrategy);
    }

    // Update strategy
    this.strategy = strategy;

    // Attach new listener
    this.strategy
      .getEventEmitter()
      .addListener(AgentListCommonStrategyEvent.updateAgentCards, this.onUpdateAgentCardsStrategy);
  }

  /**
   * Returns the strategy
   */
  public getStrategy(): AgentListCommonStrategyInterface {
    if (!this.strategy) {
      throw new Error('AgentListCommonViewStore.strategy has not been set before calling getStrategy()');
    }

    return this.strategy;
  }

  /**
   * @inheritDoc
   */
  public initialize(options?: DataStoreOptionsInterface): void {
    this.state = {
      agents: [],
      disabled: false,
      forSale: false,
    };

    this.initLeadEmail();
    this.observers();
  }

  /**
   * @inheritDoc
   */
  public getState(): AgentListCommonTemplatePropsInterface {
    return this.state;
  }

  /**
   * Update agents
   */
  public updateAgents(
    agentModels: AgentModel[],
    statsContextLocal: StatsContextLocalInterface,
    itemPerPage?: number
  ): void {
    if (!itemPerPage && statsContextLocal.pagination) {
      itemPerPage = statsContextLocal.pagination.itemPerPage;
    }

    const agents = <AgentCardCommonTemplatePropsInterface[]>agentModels.map((agentModel, index) => {
      // Default position without pagination
      let position = index + 1;

      // Pagination context
      if (statsContextLocal.pagination) {
        // Add previous records count
        position += (statsContextLocal.pagination.pageCurrent - 1) * itemPerPage;
      }

      // Register agent in stats data service
      this.statsDataService.getAgentStore().add(this.agentStatsDataAdapter.getData(agentModel, position, index + 1));

      // Log stats
      this.statsService.agentLoad(parseInt(agentModel.id, 10), statsContextLocal);

      return {
        agent: agentModel,
        action: {
          showPhoneNumber: false,
        },
        onCreateTemplate: this.onCreateTemplate,
        onClickAgent: this.onClickAgent,
        onClickEmail: this.onClickEmail,
        onClickCall: this.onClickCall,
      };
    });

    // Keep stats local context
    this.statsContextLocal = statsContextLocal;

    // Update state
    this.setState(
      {
        ...this.state,
        agents,
      },
      this.updateAgents
    );
  }

  /**
   * @inheritDoc
   */
  public updateViewport(): void {
    // View disabled
    if (this.state.disabled === true) {
      // Don't update the viewport
      return;
    }

    // For each agent
    const iMax = this.state.agents.length;
    for (let i = 0; i < iMax; i++) {
      // Reset watcher (which will check the element's position within the viewport)
      this.browserViewport.resetWatcher(String(i));
    }
  }

  /**
   * Updates in the viewport the HTML element linked to the agent's index within the list
   */
  public updateHtmlElement(index: number, element: HTMLElement): void {
    this.browserViewport.registerWatcher(String(index), element);
  }

  /**
   * Update agent ad info
   */
  public updateAgentAdInfo(info: AgentListCommonAdInfoInterface): void {
    this.setState({
      ...this.state,
      forSale: info.forSale,
    });
  }

  /**
   * @inheritDoc
   */
  public updateDisabled(disabled: boolean): void {
    // Update state
    this.state.disabled = disabled;
    this.setState(this.state, this.updateDisabled);

    // Update viewport
    this.updateViewport();
  }

  /**
   * Update agent cards from strategy
   */
  public updateAgentCards(agentCards: AgentCardCommonTemplatePropsInterface[]): void {
    this.setState(
      {
        ...this.state,
        agents: agentCards,
      },
      this.updateAgentCards
    );
  }

  /**
   * Attach observers
   */
  private observers(): void {
    // Browser viewport
    this.browserViewport.getEventEmitter().addListener(DomElementEvent.enterViewport, this.onEnterViewport);
  }

  /**
   * Initialize lead email form
   */
  private initLeadEmail(): void {
    this.leadEmailForm.initialize();

    // Lead email form
    this.leadEmailForm.getEventEmitter().addListener(LeadEmailFormEvent.submit, this.onSubmitEmail);
  }

  /**
   * Clicked card
   *
   * @param e - Event
   */
  private onClickAgent = (e: Event, agentModel: AgentModel) => {
    const clickedCta = domIsChildOf(
      <HTMLElement>e.target,
      domQuerySelectorAll(<HTMLElement>e.currentTarget, this.cssSelector.cta)
    );

    // Clicked a CTA
    if (clickedCta) {
      return;
    }

    // Log stats
    this.statsService.agentClick(
      parseInt(agentModel.id, 10),
      objectMerge({}, this.statsContextLocal, {
        authentication: {
          attributionId: this.statsAttributionService.push(StatsEntityEnum.agent, agentModel.id),
        },
      })
    );

    // TODO-FE[later]: Add a unit test to make sure location change is called after stats call
    this.windowService.getNative().location.href = agentModel.links.profile;
  };

  /**
   * Clicked email button
   *
   * @param e - Event
   */
  private onClickEmail = (e: Event, agentModel: AgentModel) => {
    e.preventDefault();

    const context: StatsContextLocalInterface = {
      ...this.statsContextLocal,
      lead: {
        medium: 'email',
        cta: 'button',
      },
    };

    // Log stats
    this.statsService.agentLeadClick(parseInt(agentModel.id, 10), context);

    this.leadEmailForm.initState();
    this.leadEmailForm.updateModel(agentModel);
    this.leadEmailForm.render();
  };

  /**
   * Handle click on card call button
   *
   * @param e - Event
   */
  private onClickCall = (e: Event, agentModel: AgentModel) => {
    const context: StatsContextLocalInterface = {
      ...this.statsContextLocal,
      lead: {
        medium: 'phone',
        cta: 'button',
      },
    };

    // Log stats
    this.statsService.agentLeadClick(parseInt(agentModel.id, 10), context);

    this.getStrategy().clickCall(e, agentModel, this.getState());
  };

  /**
   * Submitted email form
   */
  private onSubmitEmail = (input: LeadEmailFormInputInterface): void => {
    const context: StatsContextLocalInterface = {
      ...this.statsContextLocal,
      lead: new LeadStatsLeadContextAdapter(input, false).getData(),
    };

    const agent = this.leadEmailForm.getModel();

    // Log stats
    this.statsService.agentLeadSend(parseInt(agent.id, 10), context);
  };

  /**
   * Created template
   */
  private onCreateTemplate = (element: HTMLElement, index: number): void => {
    this.updateHtmlElement(index, element);
  };

  /**
   * An element entered the browser viewport
   */
  private onEnterViewport = (watcherId: string): void => {
    // View is disabled
    if (this.state.disabled === true) {
      return;
    }

    // Agent card doesn't exist
    if (!this.state.agents[parseInt(watcherId, 10)]) {
      return;
    }

    // Log stats
    this.statsService.agentImpression(
      parseInt(this.state.agents[parseInt(watcherId, 10)].agent.id, 10),
      this.statsContextLocal
    );
  };

  /**
   * Updated agent cards from strategy
   */
  private onUpdateAgentCardsStrategy = (agentCards: AgentCardCommonTemplatePropsInterface[]): void => {
    this.updateAgentCards(agentCards);
  };
}
